"use client";

import { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import HistoryTable from "./HistoryTable";
import {
  fetchHistory,
  updateFilters,
  setPage,
  clearHistory,
} from "../../../redux/features/historySlice";
import DateSelector from "../HomePage/DateSelector";
import { useLocation } from "react-router-dom";
import { useCountries, useVisaTypeFilters } from "../../client/dashboard/staticData";
import { setGlobalCountries } from "../../../redux/features/countriesSlice";
import { FiChevronDown } from "react-icons/fi";
import LimitDropdown from '../../common/LimitDropdown';

export default function MainLeads() {
  const dispatch = useDispatch();
  const location = useLocation();
  const countries = useCountries();
  const visaTypeFilters = useVisaTypeFilters();
  const isAgentView = String(location?.pathname || "").includes("/agent");
  const {
    stateFromMonth,
    stateFromYear,
    stateToMonth,
    stateToYear,
    stateCategory,
    stateStatus,
  } = location.state || {};
  // Get history data and filters from Redux
  const {
    history = { applications: [], currentPage: 1, totalPages: 1 },
    isLoading = false,
    filters,
  } = useSelector((state) => state.history) || {
    history: { applications: [], currentPage: 1, totalPages: 1 },
    isLoading: false,
    filters: {},
  };

  const {
    fromMonth,
    fromYear,
    toMonth,
    toYear,
    category: selectedCategory,
    page,
    lastFetchParams,
  } = filters;

  const currentDate = moment();
  const currentMonth = moment().format("MMMM");

  // Local UI filters - initialize with stateStatus if provided, otherwise "All"
  const [selectedStatus, setSelectedStatus] = useState(stateStatus || "All");
  const [historyLimit, setHistoryLimit] = useState(10)
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const globalCountries = useSelector((state) => state.globalCountries.globalCountries);
  const [selectedCountries, setSelectedCountries] = useState(globalCountries || []);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);

  const statusFilters = [
    "All",
    "Withdrawn",
    "Cancelled",
    "Finalized",
    "Deleted",
  ];

  // Simple fetch function like in Leads
  const fetchHistoryData = useCallback(() => {
    const params = {
      page,
      limit: historyLimit,
      category: selectedCategory,
    };

    if (fromMonth && fromYear) {
      params.fromDate = `${fromYear}-${String(fromMonth).padStart(2, "0")}-01`;
    }

    if (toMonth && toYear) {
      const endDay = moment(`${toYear}-${toMonth}`, "YYYY-M").daysInMonth();
      params.toDate = `${toYear}-${String(toMonth).padStart(2, "0")}-${String(endDay).padStart(2, "0")}`;
    }

    // Include status when not 'All'
    if (selectedStatus && selectedStatus !== "All") {
      params.status = selectedStatus;
    }

    // Include countries filter
    if (selectedCountries && selectedCountries.length > 0) {
      params.countries = selectedCountries;
    }

    // Include search term (search by name or unique id)
    if (searchTerm && searchTerm.trim()) {
      params.searchString = searchTerm.trim();
    }

    dispatch(fetchHistory(params));
  }, [dispatch, page, selectedCategory, historyLimit, fromMonth, fromYear, toMonth, toYear, selectedStatus, selectedCountries, searchTerm]);

  // Handle category change
  const handleCategoryChange = (category) => {
    dispatch(updateFilters({ category }));
  };

  // Handle country checkbox change
  const handleCheckboxChange = (countryName) => {
    setSelectedCountries((prevSelected) => {
      const updatedSelection = prevSelected.includes(countryName)
        ? prevSelected.filter((name) => name !== countryName)
        : [...prevSelected, countryName]
      dispatch(setGlobalCountries(updatedSelection))
      return updatedSelection
    })
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  // Handle date change
  const handleDateChange = (type, value) => {
    switch (type) {
      case "fromMonth":
        dispatch(updateFilters({ fromMonth: value }));
        break;
      case "fromYear":
        dispatch(updateFilters({ fromYear: value }));
        break;
      case "toMonth":
        dispatch(updateFilters({ toMonth: value }));
        break;
      case "toYear":
        dispatch(updateFilters({ toYear: value }));
        break;
      default:
        break;
    }
  };

  // Custom date selector component
  const CustomDateSelector = () => (
    <DateSelector
      className="flex w-full flex-col gap-3 sm:flex-row md:w-auto md:[&>div]:max-w-[160px] md:[&>div]:flex-none"
      fromMonth={fromMonth}
      setFromMonth={(value) => handleDateChange("fromMonth", value)}
      fromYear={fromYear}
      setFromYear={(value) => handleDateChange("fromYear", value)}
      toMonth={toMonth}
      setToMonth={(value) => handleDateChange("toMonth", value)}
      toYear={toYear}
      setToYear={(value) => handleDateChange("toYear", value)}
    />
  );

  // Handle initial mount - only fetch once on mount
  useEffect(() => {
    if (!initialLoadDone) {
      // dispatch(fetchHistoryData()); // Wait, this function logic is buggy if it's defined outside useEffect and depends on state.
      // But fetchHistoryData is defined with useCallback and deps.
      // Actually fetchHistoryData() call is safe here because it's a function.
      fetchHistoryData();
      setInitialLoadDone(true);
    }
    
    return () => {
      dispatch(clearHistory());
    };
  }, []);

  // Handle global countries change - update local state
  useEffect(() => {
    setSelectedCountries(globalCountries);
  }, [globalCountries]);

  // Fetch when filters change (but skip if initial load hasn't happened)
  useEffect(() => {
    if (initialLoadDone) {
      fetchHistoryData();
    }
  }, [fromMonth, fromYear, toMonth, toYear, selectedCategory, selectedStatus, selectedCountries, searchTerm, page, fetchHistoryData]);

  // Reset to page 1 when status changes to avoid out-of-range pages
  useEffect(() => {
    if (initialLoadDone) {
      dispatch(setPage(1));
    }
  }, [dispatch, selectedStatus, selectedCountries, searchTerm]);

  // Initialize filters from navigation state on mount
  useEffect(() => {
    if (stateFromMonth) dispatch(updateFilters({ fromMonth: stateFromMonth }));
    if (stateFromYear) dispatch(updateFilters({ fromYear: stateFromYear }));
    if (stateToMonth) dispatch(updateFilters({ toMonth: stateToMonth }));
    if (stateToYear) dispatch(updateFilters({ toYear: stateToYear }));
    if (stateCategory) dispatch(updateFilters({ category: stateCategory }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search input -> term
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Filter data based on selected category (guard against undefined)
  const apps = Array.isArray(history?.applications) ? history.applications : [];

  const filteredData = useMemo(() => {
    const byVisa = selectedCategory === "All"
      ? apps
      : apps.filter(
        (application) =>
          application.visaCategory &&
          String(application.visaCategory).toLowerCase() === String(selectedCategory).toLowerCase(),
      );


    // Base status filter
    const byStatusBase = byVisa.filter((row) => {
      if (selectedStatus === "All") return true;
      const s = String(row.status || row.visaStatus || "");
      switch (selectedStatus) {
        case "Withdrawn":
          return "WithdrawnByAgent";
        case "Cancelled":
          return "cancelled" || "WithdrawnByClient";
        case "Finalized":
          return "Finalized"
        default:
          return true;
      }
    });

    // Agent view should not show client-withdrawn records in history
    const byStatus = isAgentView
      ? byStatusBase.filter((row) => String(row.status || "") !== "WithdrawnByClient")
      : byStatusBase;

    // Sort by date (latest first) - handle locally
    const sorted = [...byStatus].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.updatedAt || 0);
      const dateB = new Date(b.createdAt || b.updatedAt || 0);
      return dateB - dateA; // Descending order (latest first)
    });

    return sorted;
  }, [apps, selectedCategory, selectedStatus, isAgentView]);

  return (
    <>
      {/* Header and Search Bar */}
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-base font-normal md:text-xl lg:text-2xl lg:font-medium">
          Client History | {currentMonth}
        </h1>
       
        {/* Search bar row */}
        <div className="flex items-center w-full sm:w-[450px] justify-end mb-4">
          <div className="relative w-full max-w-xl">
            <input
              type="text"
              className="w-full rounded-full border border-gray-300 py-2.5 px-4 focus:border-blue-500 focus:outline-none"
              placeholder="Search by name or ID"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Country Dropdown - Mobile Only */}
      <div className="mb-4 flex md:hidden justify-start">
        <div className="relative">
          <button
            onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
            className="flex items-center space-x-2 rounded-full p-1.5 md:p-2 text-sm border border-[#87CA60]/60 bg-white drop-shadow-md text-black min-w-[200px] justify-between"
          >
            <div className="flex items-center space-x-2">
              {selectedCountries.length > 0 ? (
                <>
                  <div className="flex items-center space-x-1">
                    {selectedCountries.length <= 8 &&
                      selectedCountries.map((name) => {
                        const country = countries.find((c) => c.name === name)
                        return country ? (
                          <img
                            key={country.name}
                            src={country.flag || "/placeholder.svg"}
                            alt={country.name}
                            className="w-6 h-6 rounded-full object-cover border border-slate-300"
                          />
                        ) : null
                      })}
                  </div>
                  {selectedCountries.length > 8 && (
                    <span className="text-sm text-gray-600">+{selectedCountries.length - 8} more</span>
                  )}
                </>
              ) : (
                <span className="text-gray-500">Select countries</span>
              )}
            </div>

            <svg
              className={`w-4 h-4 transition-transform ${isCountryDropdownOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isCountryDropdownOpen && (
            <div className="absolute left-0 mt-2 w-[250px] bg-white border rounded-lg shadow-lg py-1 z-10 max-h-60 overflow-y-auto">
              {countries.map((country) => (
                <label
                  key={country.name}
                  className="w-full flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 space-x-2"
                >
                  <input
                    type="checkbox"
                    checked={selectedCountries.includes(country.name)}
                    onChange={() => handleCheckboxChange(country.name)}
                    className="cursor-pointer"
                  />
                  <img
                    src={country.flag || "/placeholder.svg"}
                    alt={country.name}
                    loading="lazy"
                    className="w-5 h-5 rounded-full object-cover"
                  />
                  <span className="text-black">{country.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dropdown for Small Screens - Visa Type */}
      <div className="md:hidden mt-4 mb-4">
        <label className="text-sm text-gray-700 mb-1 block">Select Visa Type</label>
        <select
          value={selectedCategory}
          onChange={(e) => handleCategoryChange(e.target.value)}
          className="w-full p-2 border rounded-md text-gray-700"
        >
          {visaTypeFilters.map((category) => (
            <option key={category?._id} value={category?.title}>
              {category?.title}
            </option>
          ))}
        </select>
      </div>

      {/* Application Status Dropdown for Small Screens */}
      <div className="md:hidden mt-4 mb-4">
        <label className="text-sm text-gray-700 mb-1 block">Application Status</label>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="w-full p-2 border rounded-md text-gray-700 text-sm md:text-base"
        >
          {statusFilters.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
      </div>

      {/* Date Filters for Small Screens */}
      <div className="md:hidden mt-4 mb-4">
        <CustomDateSelector />
      </div>

      {/* Filters row: Desktop Only */}
      <div className="hidden md:block w-full mb-6">
        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 md:flex md:flex-wrap md:flex-1 md:items-end md:gap-2">
            <div className="relative w-full md:w-[180px] md:flex-none">
              <select
                className="w-full max-w-full appearance-none truncate rounded border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#005787]"
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                title={selectedCategory}
              >
                {visaTypeFilters.map((category) => (
                  <option key={category?._id} value={category?.title}>
                    {category?.title}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
            </div>

            <div className="relative w-full md:w-[180px] md:flex-none">
              <select
                className="w-full max-w-full appearance-none truncate rounded border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#005787]"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                title={selectedStatus}
              >
                {statusFilters.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
            </div>
          </div>

          <div className="flex justify-end md:justify-start md:flex-none md:min-w-[260px]">
            <div className="w-full md:w-auto">
              <CustomDateSelector />
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {filteredData?.length > 0 ? (
            <HistoryTable tableData={filteredData} />
          ) : (
            <p className="text-center text-red-500 pt-7">No records found.</p>
          )}

          {/* Pagination */}
            <div className="flex items-center justify-center gap-2 mt-4">
          {history.totalPages > 1 && (
          <>
              <button
                onClick={() => handlePageChange(Math.max(page - 1, 1))}
                disabled={page === 1}
                className={`px-2 md:px-3 py-2 rounded-md ${page === 1
                  ? "bg-gray-200/50 text-gray-400 cursor-default"
                  : "bg-gray-100 text-[#005787] hover:bg-gray-300"
                  }`}
              >
                &lt;
              </button>

              {/* Page Numbers */}
              {Array.from({ length: history.totalPages }).map((_, index) => {
                const pageNumber = index + 1;
                if (
                  pageNumber === 1 ||
                  pageNumber === history.totalPages ||
                  (pageNumber >= page - 1 && pageNumber <= page + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`px-3 md:px-4 py-1.5 md:py-2 rounded-md border ${page === pageNumber
                        ? "bg-[#005787] text-white border-[#334155]"
                        : "bg-gray-100 text-[#005787] hover:bg-gray-300 border-gray-200"
                        }`}
                    >
                      {pageNumber}
                    </button>
                  );
                }
                if (pageNumber === page - 2 || pageNumber === page + 2) {
                  return (
                    <span key={pageNumber} className="px-2 py-1 text-gray-500">
                      ...
                    </span>
                  );
                }
                return null;
              })}

              {/* Next Button */}
              <button
                onClick={() =>
                  handlePageChange(Math.min(page + 1, history.totalPages))
                }
                disabled={page === history.totalPages}
                className={`px-2 md:px-3 py-2 rounded-md ${page === history.totalPages
                  ? "bg-gray-200/50 text-gray-400 cursor-default"
                  : "bg-gray-100 h text-[#005787] hover:bg-gray-300"
                  }`}
              >
                &gt;
              </button>
          </>
          )}
              <div className="hidden md:flex items-center gap-3">
              <LimitDropdown value={historyLimit} onChange={(v) => { setHistoryLimit(v); dispatch(setPage(1)); }} />
            </div>
            </div>
        </>
      )}
    </>
  );
}